<template>
    <button
        :class="['wishlist-icon__cta', customClass, { 'is-wishlisted': isWishlisted }]"
        @click.stop="toggleWishlist"
        @mouseenter="isHovered = true"
        @mouseleave="isHovered = false"
        :aria-label="`${isWishlisted ? `Remove ${product.title} from wishlist` : `Add ${product.title} to wishlist`}`" 
        :aria-pressed="isWishlisted ? true : false" 
        :disabled="isLoading"
    >
        <svg
            v-if="newProductCard"
            class="wishlist-icon__svg-2026"
            width="18"
            height="17"
            viewBox="0 0 18 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <path
                v-if="isWishlisted"
                d="M12.8 0C11.264 0 9.78979 0.730621 8.82759 1.87616C7.86538 0.730621 6.39117 0 4.85517 0C2.13628 0 0 2.17382 0 4.96101C0 8.36155 3.00138 11.1487 7.54759 15.3611L8.82759 16.5517L10.1076 15.3611C14.6538 11.1487 17.6552 8.36155 17.6552 4.96101C17.6552 2.17382 15.5189 0 12.8 0Z"
                fill="currentColor"
            />
            <path
                v-else
                d="M8.91586 14.0261L8.82759 14.1163L8.73048 14.0261C4.53738 10.1385 1.76552 7.56779 1.76552 4.96101C1.76552 3.157 3.08966 1.804 4.85517 1.804C6.21462 1.804 7.53876 2.706 8.00662 3.93273H9.64855C10.1164 2.706 11.4406 1.804 12.8 1.804C14.5655 1.804 15.8897 3.157 15.8897 4.96101C15.8897 7.56779 13.1178 10.1385 8.91586 14.0261ZM12.8 0C11.264 0 9.78979 0.730621 8.82759 1.87616C7.86538 0.730621 6.39117 0 4.85517 0C2.13628 0 0 2.17382 0 4.96101C0 8.36155 3.00138 11.1487 7.54759 15.3611L8.82759 16.5517L10.1076 15.3611C14.6538 11.1487 17.6552 8.36155 17.6552 4.96101C17.6552 2.17382 15.5189 0 12.8 0Z"
                fill="currentColor"
            />
        </svg>
        <svg v-else aria-hidden="true">
            <use :xlink:href="iconID"></use>
        </svg>
    </button>
</template>

<script setup>
import { toRefs, ref, computed, watch } from 'vue';
import { useStore } from 'vuex';

const store = useStore();

const props = defineProps({
    product: {
        type: Object,
        default: {}
    },
    selectedVariantDetails: {
        type: Object,
        default: {}
    },
    customClass: {
        type: String,
        default: ''
    },
    showOptions: {
        type: Boolean,
        default: false
    },
    firstOptionName: {
        type: String,
        default: ''
    },
    isGreenVariant: {
        type: Boolean,
        default: false,
    },
    newProductCard: {
        type: Boolean,
        default: false,
    }
});

const { product, selectedVariantDetails, showOptions, firstOptionName, isGreenVariant, newProductCard } = toRefs(props);
const currentVariantDetails = ref(selectedVariantDetails.value);
const isHovered = ref(false);

const isLoading = computed(() => store.state.isWishlistLoading);
const wishlistedProducts = computed(() => store.state.wishlistProducts);
const isWishlisted = computed(() => {
    // Find the item in wishlistedProducts matching the conditions
    return wishlistedProducts.value?.find(item => {
        // Check if options are shown and if this item matches the current product
        if (showOptions.value && item.empi == product.value.id) {
            const { variantDetails } = item.cprops;

            // If 'Color' is the first option, match it with the selected variant details
            if (firstOptionName.value === 'Color') {
                return variantDetails[firstOptionName.value] === selectedVariantDetails.value[firstOptionName.value];
            }

            // For non-Color options, return true when the item matches the current product variant
            return true;
        }

        // Fallback to checking if this item's 'epi' matches the selected variant
        return item.epi == currentVariantDetails.value?.selectedVariant?.id;
    });
});

const iconID = computed(() => {
    if (isGreenVariant.value) {
        if (isHovered.value && !isWishlisted.value) {
            return '#wishlist-hover-icon-green';
        }
        return isWishlisted.value ? '#wishlist-selected-icon-green' : '#wishlist-icon-green';
    } else {
        return isWishlisted.value ? '#wishlist-selected-icon' : '#wishlist-icon';
    }
});

const toggleWishlist = (event) => {
    event.preventDefault();
    window.toggleWishlist(product.value, currentVariantDetails.value, isWishlisted.value, showOptions.value, firstOptionName.value);
};

watch(selectedVariantDetails, (val) => {
    currentVariantDetails.value = val;
});

</script>
